import { useState, useCallback, useRef } from 'react'
import {
  generateShots,
  groupShotsToScenes,
  getTaskStatus,
  generateDraftVideo
} from '../../../services/api/aiVideoGeneration'
import { normalizeSceneAudioHistory } from '../utils/audioHistory'

const MAX_RETRIES = 10
const POLL_INTERVAL = 10000 // Increased to 10s as requested

/**
 * Custom hook to manage shot generation for Draft Mode
 * Simplified version: No auto-location generation, no auto-audio clustering
 */
export const useDraftShotsGeneration = ({ parseJSONSafely, videoId } = {}) => {
  const isFetchingShots = useRef(false)
  const pollingTaskRef = useRef(null) // To prevent concurrent polling for the same or different tasks
  const [shotsGenerationProgress, setShotsGenerationProgress] = useState(0)
  const [draftGenerationProgress, setDraftGenerationProgress] = useState(0)
  const [isClustering, setIsClustering] = useState(false)
  const [clusteringProgress, setClusteringProgress] = useState(0)
  const [shotsData, setShotsData] = useState(null) // { shotList, characterMap, locationMap }
  const [error, setError] = useState(null)

  /**
   * Format scene object from API response
   * (Copied from useShotsGeneration to ensure consistency)
   */
  const formatSceneObj = useCallback(sceneObj => {
    return Object.entries(sceneObj)
      .map(([sceneKey, sceneValue]) => {
        const { scene_location = '', shots = [] } = sceneValue

        // Normalize shots to a consistent array of { key, value }
        let shotsToProcess = []
        if (Array.isArray(shots)) {
          shotsToProcess = shots.map(s => {
            const k = Object.keys(s)[0]
            return { key: k, value: s[k] }
          })
        } else if (typeof shots === 'object' && shots !== null) {
          shotsToProcess = Object.entries(shots).map(([k, v]) => ({
            key: k,
            value: v
          }))
        }

        const formattedShots = shotsToProcess.map(
          ({ key: shotKey, value: shotValue }, index) => {
            return {
              ...shotValue,
              id: `${sceneKey}-shot-${index + 1}`,
              key: shotKey,
              title: shotValue.title || '',
              initial_image_prompt:
                shotValue['initial image prompt'] ||
                shotValue.initial_image_prompt ||
                '',
              scenery: shotValue.scenery || '',
              shot_direction:
                shotValue['shot direction'] || shotValue.shot_direction || '',
              video_prompt:
                shotValue['video prompt'] || shotValue.video_prompt || '',
              voiceover_text:
                shotValue['voiceover text'] || shotValue.voiceover_text || '',
              lighting: shotValue.lighting || '',
              dialogue: shotValue.dialogue || {},
              characters: shotValue.characters || [],
              video_duration: shotValue.video_duration || '',
              vidSrc: shotValue.video_url || shotValue.vidSrc || ''
            }
          }
        )

        return {
          id: sceneKey,
          title: sceneKey,
          description: scene_location,
          shots: formattedShots,
          aggregated_voiceover: sceneValue.aggregated_voiceover || '',
          audio_duration: sceneValue.audio_duration || 0,
          audio_duration_estimated:
            sceneValue.audio_duration_estimated || false,
          estimated_audio_duration: sceneValue.estimated_audio_duration || null,
          audio_link: sceneValue.audio_link || '',
          audio_history: sceneValue.audio_history || [],
          scene_location: scene_location
        }
      })
      .map(normalizeSceneAudioHistory)
  }, [])

  /**
   * Handle scene mapping from shot data
   */
  const handleScenesMapping = useCallback(
    async data => {
      try {
        const payload = {
          input_data: data,
          video_id: videoId
        }

        const response = await groupShotsToScenes(payload)

        if (response.data && response.data.task_id) {
          setIsClustering(true)
          setClusteringProgress(0)
          await pollTaskStatus(response.data.task_id, data)
        } else {
          const sceneData = response.data && response.data.output
          const formattedSceneData = formatSceneObj(sceneData)

          // Set complete data
          setShotsData({
            shotList: formattedSceneData,
            shotsApiRes: data,
            locationMap: data.location_map
              ? parseJSONSafely?.(data.location_map)
              : null,
            characterMap: data.character_map
              ? parseJSONSafely?.(data.character_map)
              : null,
            wardrobe: data.wardrobe ? parseJSONSafely?.(data.wardrobe) : null,
            outfitMap: data.outfit_map
              ? parseJSONSafely?.(data.outfit_map)
              : null,
            propMap: data.prop_map ? parseJSONSafely?.(data.prop_map) : null,
            props: data.props ? parseJSONSafely?.(data.props) : null,
            characterVoices: data.character_voices
              ? parseJSONSafely?.(data.character_voices)
              : null,
            selectedVoice: data.selected_voice || null
          })

          isFetchingShots.current = false
        }
      } catch (error) {
        console.error('Draft scene mapping failed:', error)
        setError(error)
        isFetchingShots.current = false
      }
    },
    [formatSceneObj, parseJSONSafely, videoId]
  )

  /**
   * Poll task status for scene grouping
   */
  const pollTaskStatus = useCallback(
    async (taskId, originalData) => {
      while (true) {
        try {
          const response = await getTaskStatus(taskId)

          if (
            response.data &&
            response.data.status === 'success' &&
            response.data.output
          ) {
            const sceneData = response.data.output
            const formattedSceneData = formatSceneObj(sceneData)

            setShotsData({
              shotList: formattedSceneData,
              shotsApiRes: originalData,
              locationMap: originalData.location_map
                ? parseJSONSafely?.(originalData.location_map)
                : null,
              characterMap: originalData.character_map
                ? parseJSONSafely?.(originalData.character_map)
                : null,
              wardrobe: originalData.wardrobe
                ? parseJSONSafely?.(originalData.wardrobe)
                : null,
              outfitMap: originalData.outfit_map
                ? parseJSONSafely?.(originalData.outfit_map)
                : null,
              propMap: originalData.prop_map
                ? parseJSONSafely?.(originalData.prop_map)
                : null,
              props: originalData.props
                ? parseJSONSafely?.(originalData.props)
                : null,
              characterVoices: originalData.character_voices
                ? parseJSONSafely?.(originalData.character_voices)
                : null,
              selectedVoice: originalData.selected_voice
            })

            setClusteringProgress(100)
            setIsClustering(false)
            isFetchingShots.current = false
            break
          } else if (response.data && response.data.status === 'failed') {
            console.error('Task failed:', response.data)
            setIsClustering(false)
            throw new Error('Task processing failed')
          } else {
            setClusteringProgress(
              response.data && response.data.progress
                ? response.data.progress
                : 0
            )
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          setIsClustering(false)
          setError(error)
          isFetchingShots.current = false
          break
        }
      }
    },
    [formatSceneObj, parseJSONSafely]
  )

  /**
   * Poll shots task status
   */
  const pollShotsTaskStatus = useCallback(
    async taskId => {
      while (true) {
        try {
          if (!isFetchingShots.current) break

          const response = await getTaskStatus(taskId)

          if (
            response.data &&
            response.data.status === 'success' &&
            response.data.output
          ) {
            const shotsData = response.data.output
            setShotsGenerationProgress(100)
            await handleScenesMapping(shotsData)
            break
          } else if (response.data && response.data.status === 'failed') {
            console.error('Draft shots generation task failed:', response.data)
            throw new Error('Shots generation failed')
          } else {
            setShotsGenerationProgress(
              response.data && response.data.progress
                ? response.data.progress
                : shotsGenerationProgress
            )
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          console.error('Error polling shots status:', error)
          setError(error)
          isFetchingShots.current = false
          break
        }
      }
    },
    [handleScenesMapping, shotsGenerationProgress]
  )

  /**
   * Fetch shots from voiceover text
   */
  const fetchDraftShots = useCallback(
    async (
      storyText,
      styleId = null,
      audioMode = 'narration',
      language = 'en',
      retryCount = 0
    ) => {
      if (retryCount === 0) {
        if (isFetchingShots.current) return
        isFetchingShots.current = true
        setError(null)
        setShotsData(null)
      }

      try {
        const finalStyleId =
          styleId === 'default_style'
            ? null
            : styleId || (typeof styleId === 'string' ? styleId : null)

        const res = await generateShots({
          story: storyText,
          style_id: finalStyleId,
          video_id: videoId,
          video_mode: audioMode,
          language
        })
        const taskId = res && res.data && res.data.task_id

        if (taskId) {
          await pollShotsTaskStatus(taskId)
        } else {
          const shotsData = res && res.data
          await handleScenesMapping(shotsData)
        }
      } catch (err) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(
            () =>
              fetchDraftShots(
                storyText,
                styleId,
                audioMode,
                language,
                retryCount + 1
              ),
            POLL_INTERVAL
          )
        } else {
          setError(err)
          isFetchingShots.current = false
        }
      }
    },
    [pollShotsTaskStatus, handleScenesMapping, videoId]
  )

  /**
   * Poll Draft Video Generation Status
   */
  const pollDraftVideo = useCallback(async (taskId, onSuccess) => {
    // If we're already polling for this task, don't start another loop
    if (pollingTaskRef.current === taskId) return

    // If we're polling for a DIFFERENT task, we should probably stop the old one or at least note this one is active
    pollingTaskRef.current = taskId
    setDraftGenerationProgress(0)

    try {
      let isComplete = false
      while (!isComplete && pollingTaskRef.current === taskId) {
        const response = await getTaskStatus(taskId)
        const status = response?.data?.status
        const progress = response?.data?.progress || 0

        setDraftGenerationProgress(progress)

        if (status === 'success' || status === 'completed') {
          isComplete = true
          pollingTaskRef.current = null
          if (onSuccess) onSuccess(response?.data?.output)
          break
        } else if (status === 'failed') {
          isComplete = true
          pollingTaskRef.current = null
          throw new Error('Draft generation failed')
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      }
    } catch (err) {
      console.error('Polling error:', err)
      pollingTaskRef.current = null
      setError(err)
      throw err
    }
  }, [])

  return {
    isFetchingShots: isFetchingShots.current,
    shotsGenerationProgress,
    draftGenerationProgress,
    isClustering,
    clusteringProgress,
    shotsData, // The Main Output
    fetchDraftShots,
    pollDraftVideo, // Expose this
    error,

    // Utilities
    formatSceneObj
  }
}

export default useDraftShotsGeneration
